import React, { useEffect, useCallback } from 'react';
import styles from './Patrols.module.css';

function formatClock(dt) {
  return dt
    .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    .replace(' AM', '\u00A0AM')
    .replace(' PM', '\u00A0PM');
}

function formatDateTime(dtStr) {
  if (!dtStr) return '';
  const d = new Date(dtStr.replace(' ', 'T'));
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) + ' ' +
         formatClock(d);
}

function formatActionLabel(action) {
  const raw = String(action || 'Checked').trim().toLowerCase();
  if (!raw || raw === 'checked' || raw === 'checkin' || raw === 'check-in' || raw === 'check in') {
    return 'Checked';
  }
  if (raw === 'complete' || raw === 'completed') {
    return 'Completed';
  }
  return raw.replace(/\b\w/g, (ch) => ch.toUpperCase());
}

export default function PatrolDetailModal({ data, onClose }) {
  const { request, checks = [] } = data;

  // Close on Escape key
  const handleKey = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  return (
    <div
      className={styles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-label="Patrol Request Details"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={styles.modalPanel}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>
            <span className={styles.modalTypeBadge}>{request.type_name || 'Patrol'}</span>
            <span className={styles.modalStatusBadge}>{request.status}</span>
          </div>
          <button
            type="button"
            className={styles.modalClose}
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Request details */}
        <div className={styles.modalBody}>
          {(request.address || request.city) && (
            <p className={styles.modalAddress}>
              📍 {[request.address, request.city].filter(Boolean).join(', ')}
            </p>
          )}

          {request.notes && (
            <div className={styles.modalNotesBlock}>
              <strong>Notes:</strong>
              <p className={styles.modalNotes}>{request.notes}</p>
            </div>
          )}

          <div className={styles.modalMeta}>
            <span>Created: {formatDateTime(request.created_at)}</span>
          </div>

          {/* Check-in history */}
          <h4 className={styles.modalHistoryTitle}>Check-In History</h4>

          {checks.length === 0 && (
            <p className={styles.empty}>No check-ins recorded yet.</p>
          )}

          {checks.length > 0 && (
            <div className={styles.modalTableWrap}>
              <table className={styles.modalTable}>
                <thead>
                  <tr>
                    <th>Date / Time</th>
                    <th className={styles.modalOfficerHeader}>Officer</th>
                    <th>Action</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {checks.map((c, i) => (
                    <tr key={c.id || i}>
                      <td className={styles.modalTime}>{formatDateTime(c.created_at)}</td>
                      <td className={styles.modalOfficer}>{c.officer_label || c.officer_name || '—'}</td>
                      <td className={styles.modalAction}>{formatActionLabel(c.action)}</td>
                      <td className={styles.modalCheckNote}>{c.note || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button type="button" className={styles.button} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
